import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Loading from "../../components/ui/loader/loader";
import Pagination from "../../components/ui/pagination/pagination";
import errorToast from "../../components/toast/errorToast";
import { systemLogsList } from "../../redux/actions/logsAction";
import { logsExportApi } from "../../redux/apis/logsApis";
import PageTableLayout from "../../components/ui/PageTableLayout/PageTableLayout";
import "./systemLogs.css";
import { useAuth } from '../../context/AuthContext';

/* ── Constants ─────────────────────────────────────────────────────────── */

const ACTION_MAP = {
    AUTH: ["LOGIN_SUCCESS", "LOGIN_FAILED", "SIGNUP", "SIGNUP_APPROVED", "SIGNUP_REJECTED", "FORGOT_PASSWORD", "PASSWORD_RESET", "TOKEN_VERIFIED"],
    PORTAL: ["PORTAL_CREATED", "PORTAL_EDITED", "PORTAL_DELETED", "PORTAL_USER_ADDED", "PORTAL_USER_REMOVED", "PORTAL_GLOSSARY_ASSIGNED", "PORTAL_GLOSSARY_REMOVED"],
    USER: ["USER_INVITED", "USER_EDITED", "USER_DELETED", "USER_REMOVED_FROM_PORTAL"],
    TRANSLATION: ["DOCUMENT_UPLOADED", "DOCUMENT_TRANSLATED", "BATCH_TRANSLATED", "HTML_TRANSLATED", "TRANSLATION_LIMIT_EXCEEDED", "TRANSLATION_EXPIRED", "TRANSLATION_COMPLETED"],
    RESOURCE: ["GLOSSARY_CSV_UPLOADED", "GLOSSARY_CREATED", "GLOSSARY_DELETED", "GLOSSARY_PORTALS_ASSIGNED", "GLOSSARY_TEXT_TRANSLATED"],
    COMPANY: ["COMPANY_REGISTERED", "COMPANY_APPROVED", "COMPANY_REJECTED"],
    SETTINGS: ["SETTINGS_UPDATED"],
    PUBSUB: ["PUBSUB_RECEIVED", "ENTITLEMENT_ACTIVE", "ENTITLEMENT_RENEWED", "ENTITLEMENT_CANCELLED", "ENTITLEMENT_DELETED", "ENTITLEMENT_PLAN_CHANGED", "ENTITLEMENT_PLAN_CHANGE_CANCELLED", "ENTITLEMENT_OFFER_ENDED", "ACCOUNT_DELETED"],
    CRON: ["CRON_EXPIRY_COMPLETED", "CRON_USAGE_REPORTED", "CRON_STATUS_UPDATED"],
};

const ALL_ACTIONS = Object.values(ACTION_MAP).flat();

const CATEGORY_STYLES = {
    AUTH: { backgroundColor: "#cfe2ff", color: "#084298" },
    PORTAL: { backgroundColor: "#e2d9f3", color: "#432874" },
    USER: { backgroundColor: "#cff4fc", color: "#055160" },
    TRANSLATION: { backgroundColor: "#d1e7dd", color: "#0a3622" },
    RESOURCE: { backgroundColor: "#d2f4ea", color: "#13795b" },
    COMPANY: { backgroundColor: "#ffe5d0", color: "#6d2a05" },
    PUBSUB: { backgroundColor: "#f7d6e6", color: "#6a1a39" },
    CRON: { backgroundColor: "#fff3cd", color: "#664d03" },
    SETTINGS: { backgroundColor: "#e2e3e5", color: "#41464b" },
};

const STATUS_CLASS = {
    SUCCESS: "badge bg-success",
    FAILED: "badge bg-danger",
    PENDING: "badge bg-warning text-dark",
};

const EMPTY_FILTERS = {
    event_category: "",
    event_action: "",
    event_status: "",
    from_date: "",
    to_date: "",
};

const EMPTY_EXPORT_FORM = { from_date: "", to_date: "" };

/* ── Helpers ────────────────────────────────────────────────────────────── */

const formatAction = (str) =>
    str.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

const CategoryBadge = ({ category }) => {
    const style = CATEGORY_STYLES[category] || { backgroundColor: "#e2e3e5", color: "#41464b" };
    return <span className="category-badge" style={style}>{category}</span>;
};

const StatusBadge = ({ status }) => (
    <span className={STATUS_CLASS[status] || "badge bg-secondary"}>{status}</span>
);

/* ── Main Component ─────────────────────────────────────────────────────── */

const SystemLogs = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [paginationData, setPaginationData] = useState({});

    const [filterForm, setFilterForm] = useState({ ...EMPTY_FILTERS });
    const [appliedFilters, setAppliedFilters] = useState({});

    const [search, setSearch] = useState("");
    const searchTimerRef = useRef(null);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setPage(1);
            setAppliedFilters((prev) => ({ ...prev, search: value }));
        }, 500);
    };

    /* Export modal */
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportForm, setExportForm] = useState({ ...EMPTY_EXPORT_FORM });

    useEffect(() => {
        document.title = "Activity Logs - Acumen Translation Hub";
    }, []);

    const buildParams = (filters) => {
        const params = { page, page_size: pageSize };
        Object.entries(filters).forEach(([k, v]) => {
            if (v) params[k] = v;
        });
        return params;
    };

    const onSuccess = (res) => {
        const { count, rows, pagination } = res.data.data;
        setLogs(rows);
        setPaginationData({
            currentPage: pagination.currentPage,
            total: count,
            totalPages: pagination.totalPages,
            nextPage: pagination.currentPage < pagination.totalPages,
        });
        setLoading(false);
    };

    const onError = (res) => {
        errorToast(res?.data?.message || "Failed to load logs");
        setLoading(false);
    };

    useEffect(() => {
        setLoading(true);
        dispatch(systemLogsList(buildParams(appliedFilters), onSuccess, onError));
    }, [page, pageSize, appliedFilters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilterForm((prev) => ({
            ...prev,
            [name]: value,
            ...(name === "event_category" ? { event_action: "" } : {}),
        }));
    };

    const handleApplyFilters = () => {
        setPage(1);
        setAppliedFilters({ ...filterForm });
    };

    const handleClearFilters = () => {
        setFilterForm({ ...EMPTY_FILTERS });
        setSearch("");
        clearTimeout(searchTimerRef.current);
        setPage(1);
        setAppliedFilters({});
    };

    /* Export uses the modal's date range — independent of the filter bar */
    const handleExportSubmit = async () => {
        if (!exportForm.from_date || !exportForm.to_date) {
            errorToast("Please set both From Date and To Date");
            return;
        }
        setExportLoading(true);
        try {
            const res = await logsExportApi({ from_date: exportForm.from_date, to_date: exportForm.to_date });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `system-logs-${exportForm.from_date}-to-${exportForm.to_date}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setShowExportModal(false);
            setExportForm({ ...EMPTY_EXPORT_FORM });
        } catch (error) {
            let message = 'Export failed';
            if (error.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const json = JSON.parse(text);
                    message = json.message || message;
                } catch (_) { /* blob was not JSON */ }
            } else {
                message = error.response?.data?.message || message;
            }
            errorToast(message);
        } finally {
            setExportLoading(false);
        }
    };

    const availableActions = filterForm.event_category
        ? ACTION_MAP[filterForm.event_category] || []
        : ALL_ACTIONS;

    /* ── Access guard ── */
    if (!isAdmin) {
        return (
            <div className="w-100 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
                <i className="bi bi-shield-lock fs_40 text-danger mb-3"></i>
                <h5 className="fw_600">Access Denied</h5>
                <p className="text-muted fs_14">This page is only accessible to company administrators.</p>
            </div>
        );
    }

    return (
        <>
            <PageTableLayout
                title="Activity Logs"
                tableClassName="table-hover align-middle"
                headerActions={
                    <button
                        className="btn btn-sm btn-outline-success ms-auto me-3 d-flex align-items-center gap-2"
                        onClick={() => setShowExportModal(true)}
                        disabled={loading}
                    >
                        <i className="bi bi-download" />
                        Export CSV
                    </button>
                }
                filters={
                    <div className="system-logs-filters p-3 border rounded bg-light mt-1">
                        <div className="row g-2 align-items-end text-start">
                            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                <label className="form-label fs_12 fw_600 mb-1">Search</label>
                                <div className="input-group input-group-sm search-group">
                                    <span className="input-group-text bg-white border-end-0">
                                        <i className="bi bi-search text-muted fs_12" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Enter user email…"
                                        value={search}
                                        onChange={handleSearchChange}
                                    />
                                    {search && (
                                        <button
                                            className="btn btn-outline-secondary border-start-0"
                                            type="button"
                                            onClick={() => {
                                                setSearch("");
                                                clearTimeout(searchTimerRef.current);
                                                setPage(1);
                                                setAppliedFilters((prev) => { const f = { ...prev }; delete f.search; return f; });
                                            }}
                                        >
                                            <i className="bi bi-x fs_13" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Category and Action are available but currently hidden */}
                            {/* <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <label className="form-label fs_12 fw_600 mb-1">Category</label>
                                <select className="form-select form-select-sm" name="event_category" value={filterForm.event_category} onChange={handleFilterChange}>
                                    <option value="">All Categories</option>
                                    {Object.keys(ACTION_MAP).map((cat) => (<option key={cat} value={cat}>{cat}</option>))}
                                </select>
                            </div>
                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <label className="form-label fs_12 fw_600 mb-1">Action</label>
                                <select className="form-select form-select-sm" name="event_action" value={filterForm.event_action} onChange={handleFilterChange}>
                                    <option value="">All Actions</option>
                                    {availableActions.map((act) => (<option key={act} value={act}>{formatAction(act)}</option>))}
                                </select>
                            </div> */}

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <label className="form-label fs_12 fw_600 mb-1">Status</label>
                                <select
                                    className="form-select form-select-sm"
                                    name="event_status"
                                    value={filterForm.event_status}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">All Statuses</option>
                                    <option value="SUCCESS">SUCCESS</option>
                                    <option value="FAILED">FAILED</option>
                                    <option value="PENDING">PENDING</option>
                                </select>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <label className="form-label fs_12 fw_600 mb-1">From Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    name="from_date"
                                    value={filterForm.from_date}
                                    onChange={handleFilterChange}
                                />
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <label className="form-label fs_12 fw_600 mb-1">To Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    name="to_date"
                                    value={filterForm.to_date}
                                    onChange={handleFilterChange}
                                />
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2 d-flex gap-2">
                                <button
                                    className="btn btn-primary btn-sm flex-grow-1"
                                    onClick={handleApplyFilters}
                                    disabled={loading}
                                >
                                    Apply
                                </button>
                                <button
                                    className="btn btn-outline-secondary btn-sm flex-grow-1"
                                    onClick={handleClearFilters}
                                    disabled={loading}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>
                }
                pagination={
                    paginationData?.total > 0 ? (
                        <Pagination
                            data={paginationData}
                            nextFunc={() => setPage((p) => p + 1)}
                            prevFunc={() => setPage((p) => p - 1)}
                            setPageRowFunc={(val) => {
                                setPage(1);
                                setPageSize(Number(val));
                            }}
                        />
                    ) : null
                }
            >
                <thead>
                    <tr className="text-start fs_13 text-nowrap">
                        <th>Timestamp</th>
                        <th>Category</th>
                        <th>Action</th>
                        <th>Status</th>
                        <th>Actor</th>
                        <th>Target</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan="7">
                                <Loading text="Loading logs..." style={{ height: "5rem" }} />
                            </td>
                        </tr>
                    ) : logs.length === 0 ? (
                        <tr>
                            <td colSpan="7" className="text-center text-muted py-5 fs_14">
                                No logs found
                            </td>
                        </tr>
                    ) : (
                        logs.map((log) => (
                            <tr key={log.id} className="text-start fs_13">
                                <td className="text-nowrap">
                                    {moment(log.createdAt).format("MMM D, YYYY h:mm A")}
                                </td>
                                <td>
                                    <CategoryBadge category={log.event_category} />
                                </td>
                                <td>{formatAction(log.event_action)}</td>
                                <td>
                                    <StatusBadge status={log.event_status} />
                                </td>
                                <td>{log.actor_email || log.actor_type || "—"}</td>
                                <td>
                                    {log.target_type
                                        ? `${log.target_type}: ${log.target_id || "—"}`
                                        : "—"}
                                </td>
                                <td>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => navigate(`/system-logs/${log.id}`)}
                                    >
                                        View
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </PageTableLayout>

            {/* Export Modal */}
            <Modal
                show={showExportModal}
                onHide={() => { setShowExportModal(false); setExportForm({ ...EMPTY_EXPORT_FORM }); }}
                centered
            >
                <Modal.Header closeButton className="border-0 pb-0">
                    <div className="d-flex align-items-center gap-3">
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: "#d1e7dd", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <i className="bi bi-file-earmark-arrow-down" style={{ fontSize: 20, color: "#0a3622" }} />
                        </div>
                        <div>
                            <Modal.Title className="fs_16 fw_600 mb-0">Export Activity Logs</Modal.Title>
                            <p className="text-muted fs_12 mb-0">Select a date range to download as CSV</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="pt-3 pb-2">
                    <div className="row g-3">
                        <div className="col-6">
                            <label className="form-label fs_12 fw_600 mb-1 text-uppercase text-secondary" style={{ letterSpacing: "0.04em" }}>From Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={exportForm.from_date}
                                max={exportForm.to_date || undefined}
                                onChange={(e) => setExportForm((prev) => ({ ...prev, from_date: e.target.value }))}
                            />
                        </div>
                        <div className="col-6">
                            <label className="form-label fs_12 fw_600 mb-1 text-uppercase text-secondary" style={{ letterSpacing: "0.04em" }}>To Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={exportForm.to_date}
                                min={exportForm.from_date || undefined}
                                onChange={(e) => setExportForm((prev) => ({ ...prev, to_date: e.target.value }))}
                            />
                        </div>
                    </div>
                    {exportForm.from_date && exportForm.to_date && (
                        <div className="mt-3 px-3 py-2 rounded d-flex align-items-center gap-2" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                            <i className="bi bi-calendar-range text-success" />
                            <span className="fs_13 text-success fw_500">
                                {moment(exportForm.from_date).format("MMM D, YYYY")}
                                <span className="text-muted fw_400 mx-1">to</span>
                                {moment(exportForm.to_date).format("MMM D, YYYY")}
                            </span>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-1 gap-2">
                    <button
                        className="btn btn-outline-secondary flex-fill"
                        onClick={() => { setShowExportModal(false); setExportForm({ ...EMPTY_EXPORT_FORM }); }}
                        disabled={exportLoading}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-success flex-fill d-flex align-items-center justify-content-center gap-2"
                        onClick={handleExportSubmit}
                        disabled={exportLoading || !exportForm.from_date || !exportForm.to_date}
                    >
                        {exportLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                Exporting...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-download" />
                                Export CSV
                            </>
                        )}
                    </button>
                </Modal.Footer>
            </Modal>

        </>
    );
};

export default SystemLogs;
